/**
 * Bulk Operation Progress Modal
 *
 * Generic progress modal for bulk operations.
 * Shows progress bar, counts, and allows cancellation.
 */

import { Modal, Progress, Space, Typography, Button } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

const { Text } = Typography

export interface BulkOperationProgressProps {
  /** Whether the modal is visible */
  visible: boolean

  /** Operation label (e.g., 'Activate', 'Delete', 'Update') */
  operation: string

  /** Total number of items to process */
  total: number

  /** Number of items completed successfully */
  completed: number

  /** Number of items that failed */
  failed: number

  /** Callback when user clicks Cancel */
  onCancel: () => void
}

export function BulkOperationProgress({
  visible,
  operation,
  total,
  completed,
  failed,
  onCancel,
}: BulkOperationProgressProps) {
  const processed = completed + failed
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0
  const remaining = total - processed

  return (
    <Modal
      open={visible}
      title={`${operation} Items`}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
      ]}
      closable={false}
      maskClosable={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Progress Bar */}
        <Progress
          percent={percent}
          status={failed > 0 ? 'exception' : 'active'}
          format={() => `${percent}%`}
        />

        {/* Status Text */}
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <LoadingOutlined />
            <Text strong>
              {operation} {processed} of {total} items...
            </Text>
          </Space>

          <Text type="secondary">Completed: {completed}</Text>

          {failed > 0 && <Text type="danger">Failed: {failed}</Text>}

          {remaining > 0 && <Text type="secondary">Remaining: {remaining}</Text>}
        </Space>

        {/* Warning */}
        {remaining > 0 && (
          <Text type="warning">
            Clicking Cancel will stop processing remaining items. Already processed items cannot be
            undone.
          </Text>
        )}
      </Space>
    </Modal>
  )
}
